import React from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Header from "../components/Header/userHeader";
import { UserFooter } from "../components";

const sections = [
  {
    title: "1. Nature of the Platform (Intermediary Status)",
    blocks: [
      "Sthanick operates strictly as a technology-based intermediary and hyperlocal aggregator. The Platform facilitates connections between Users and three distinct categories of independent partners:",
    ],
    checklist: [
      [
        "Merchants (Sthanick Fulfilled)",
        "Local sellers offering goods delivered by Sthanick's independent Delivery Partners.",
      ],
      [
        "Merchants (Self-Delivery)",
        "Local sellers offering goods who handle their own logistics and fulfillment.",
      ],
      [
        "Service Providers",
        "Independent professionals and tradespeople offering hyperlocal services.",
      ],
    ],
    note: "Arthavyavas Private Limited does not own, sell, resell, provide, control, manage, or offer any of the goods or services listed on the Platform.",
  },
  {
    title: "2. Acknowledgment & Eligibility",
    blocks: [
      "These Terms and Conditions govern the use of this Service and the agreement that operates between you and the Company. Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms and Conditions and our Privacy Policy.",
      "You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service. If you disagree with any part of these Terms, you may not access the Service.",
    ],
  },
  {
    title: "3. E-Commerce Goods & Orders",
    subsections: [
      [
        "3.1. Contract of Sale",
        "When placing an order for physical goods on Sthanick, the User enters into a direct, legally binding contract with the respective Merchant.",
      ],
      [
        "3.2. Product Liability",
        "The Merchant is solely responsible for the quality, hygiene, safety, packaging, legality, and warranty of the goods sold. Sthanick bears no liability for defective, stale, or damaged items.",
      ],
      [
        "3.3. Sthanick Logistics",
        "For orders delivered by Sthanick Delivery Partners, the partners act as independent contractors. Sthanick merely facilitates the logistics and is not liable for acts, omissions, or delays caused by traffic, weather, or partner conduct.",
      ],
      [
        "3.4. Vendor Self-Delivery",
        'If a Merchant elects to fulfill orders using their own personnel ("Self-Delivery"), the Merchant assumes absolute responsibility for the timely and safe transport of the goods. Sthanick enforces strict estimated times of arrival (ETAs) internally, but shall bear no liability to the User for any loss, damage, theft, or delay during transit under Self-Delivery.',
      ],
      [
        "3.5. Variable Pricing and Substitutions",
        "For certain goods, the price displayed is an estimate based on average weight. The final price charged will be calculated based on the actual weight packed. If an item is out of stock, the Merchant may offer a substitution.",
      ],
      [
        "3.6. Unattended Deliveries",
        'If a User selects a "Leave at Door" option, or is unreachable upon arrival, the Delivery Partner may leave the goods at the designated address. Upon drop-off, the risk of loss passes entirely to the User.',
      ],
      [
        "3.7. Age-Restricted Items",
        "Certain items are subject to age restrictions. The User agrees to provide valid government-issued photo identification upon delivery.",
      ],
    ],
  },
  {
    title: "4. Hyperlocal Service Providers (Directory Services)",
    subsections: [
      [
        "4.1. Platform as a Lead Generator",
        'For the "Services" category, Sthanick acts strictly as an online directory and lead-generation tool, displaying the contact information and profiles of independent Service Providers.',
      ],
      [
        "4.2. No Guarantee or Warranty",
        "Sthanick does not endorse, verify, or guarantee the skills, qualifications, licenses, or background of any Service Provider.",
      ],
      [
        "4.3. Cash on Delivery (COD) & Offline Settlement",
        "For service requests completed via COD, the User agrees that all negotiations, finalized pricing, and payments occur strictly off-platform directly between the User and the Service Provider. Sthanick does not collect, hold, or facilitate the transfer of these offline funds.",
      ],
      [
        "4.4. Absolute Waiver of Liability",
        "Once a Service Provider accepts a request and arrives at the location, the transaction is entirely between those two parties. We shall not be responsible or liable for unsatisfactory work, pricing disputes, or property damage.",
      ],
      [
        "4.5. Dispute Resolution",
        "Any dispute arising from a requested service must be resolved directly between the User and the Service Provider.",
      ],
    ],
  },
  {
    title: "5. Payments, Cancellations & Refunds",
    subsections: [
      [
        "5.1. Payment Gateway",
        "All online payments for goods are securely processed through our authorized payment partner, Razorpay.",
      ],
      [
        "5.2. Cancellations",
        "Users may cancel an order without penalty only before the Merchant has accepted and begun preparing/packaging the order. Cancellations after this point may incur a cancellation fee up to the total order value.",
      ],
      [
        "5.3. Refunds",
        "Refunds are handled on a case-by-case basis. If a refund is approved, the amount will be credited back to the original payment source via Razorpay within standard banking timelines. Sthanick does not process refunds for offline service transactions.",
      ],
    ],
  },
  {
    title: "6. User Conduct and Restrictions",
    blocks: ["You agree not to:"],
    bullets: [
      "Use the Platform for any illegal or unauthorized purpose.",
      "Post false, malicious, or defamatory reviews.",
      "Exploit platform glitches, promo codes, or referral programs fraudulently.",
      "Harass, abuse, or misbehave with Delivery Partners or Merchants.",
    ],
  },
  {
    title: "7. Pilot Phase Operations",
    blocks: [
      "During the initial rollout phase in the Pocharam geographic zone, service availability, delivery radii, and platform features may be limited, modified, or subject to localized stress-testing.",
    ],
  },
];

const moreSections = [
  {
    title: "8. Links to Other Websites",
    blocks: [
      "Our Service may contain links to third-party websites or services that are not owned or controlled by the Company.",
      "The Company has no control over those websites and is not responsible for related content, policies, goods, services, damage, or loss.",
    ],
  },
  {
    title: "9. Termination",
    blocks: [
      "We may terminate or suspend your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms and Conditions. Upon termination, your right to use the Service will cease immediately.",
    ],
  },
  {
    title: '10. "AS IS" and "AS AVAILABLE" Disclaimer',
    blocks: [
      'The Service is provided to you "AS IS" and "AS AVAILABLE" and with all faults and defects without warranty of any kind.',
      "The Company makes no representation or warranty that the Service will meet your requirements, achieve intended results, operate without interruption, or be error-free.",
    ],
  },
  {
    title: "11. Limitation of Liability",
    blocks: [
      "To the maximum extent permitted by law, Arthavyavas Private Limited, its directors, employees, and affiliates shall not be liable for any special, incidental, indirect, consequential, or punitive damages whatsoever arising out of the Platform or Service.",
      "If we are found liable for any reason, our total financial responsibility to you is strictly capped at the exact amount you paid through our service.",
    ],
  },
  {
    title: "12. Governing Law & Dispute Resolution",
    blocks: [
      "These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts in Hyderabad, Telangana.",
      "Dispute Resolution: If you have any concern or dispute about the Service, you agree to first try to resolve the dispute informally by contacting the Company.",
    ],
  },
  {
    title: "13. Severability and Waiver",
    subsections: [
      [
        "13.1. Severability",
        "If any provision of these Terms is held to be unenforceable or invalid, such provision will be interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.",
      ],
      [
        "13.2. Waiver",
        "Except as provided herein, the failure to exercise a right or to require performance of an obligation under these Terms shall not affect a party's ability to exercise such right or require such performance at any time thereafter.",
      ],
    ],
  },
  {
    title: "14. Intellectual Property",
    blocks: [
      "All content on the Platform, including but not limited to the Sthanick name, logos, text, and software, is the exclusive property of Arthavyavas Private Limited and is protected by applicable intellectual property laws. Users agree not to copy, modify, or distribute any platform content without our express written consent.",
    ],
  },
  {
    title: "15. Changes to These Terms",
    blocks: [
      "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.",
      "By continuing to access or use the Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, in whole or in part, please stop using the website and the Service.",
    ],
  },
];

const Termsconditions = () => {
  return (
    <Box
      sx={{
        bgcolor: "#fbf5ec",
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Header />
      <Box
        component="section"
        sx={{
          bgcolor: "#ff6b0b",
          color: "#fff",
          textAlign: "center",
          pt: { xs: 7, md: 6.3 },
          pb: { xs: 7, md: 8.4 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 38, md: 48 },
              lineHeight: 1.1,
              fontWeight: 900,
              letterSpacing: 0,
              mb: 2.1,
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 16, md: 18 },
              letterSpacing: 1.1,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            Please read these terms and conditions carefully before using our
            platform.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ maxWidth: 880, px: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            // mt: { xs: -2.5, md: -2 },
            mt: 5,
            mb: 5,
            borderRadius: "12px 12px 10px 10px",
            borderTop: "2px solid #f05a1a",
            bgcolor: "#fff",
            boxShadow: "0 16px 38px rgba(51, 36, 22, 0.12)",
            px: { xs: 3, sm: 4, md: 5 },
            py: { xs: 4, md: 4.6 },
          }}
        >
          <Typography sx={paragraphSx}>
            <Box component="strong" sx={{ color: "#243145", fontWeight: 900 }}>
              Welcome to Sthanick!
            </Box>{" "}
            These Terms and Conditions ("Terms") govern access to and use of the
            Sthanick mobile application and website (collectively, the
            "Platform"), owned and operated by Arthavyavas Private Limited
            ("Company", "we", "us", or "our").
          </Typography>
          <Typography sx={{ ...paragraphSx, mb: 4.8 }}>
            By accessing, registering, or using the Platform, users ("Users",
            "you") agree to be bound by these Terms.
          </Typography>

          {[...sections, ...moreSections].map((section) => (
            <TermsSection key={section.title} section={section} />
          ))}
        </Paper>
      </Container>

      {/* <Box
        component="footer"
        sx={{
          bgcolor: "#070c15",
          color: "#fff",
          textAlign: "center",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          py: 3.4,
        }}
      >
        <Stack alignItems="center" spacing={1.4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.8}
          >
            <LocationOnOutlinedIcon sx={{ color: "#ff6b1a", fontSize: 24 }} />
            <Typography sx={{ fontSize: 23, fontWeight: 900 }}>
              Sthanick
            </Typography>
          </Stack>
          <Typography sx={{ color: "#8791a4", fontSize: 14 }}>
            An initiative by Arthavyavas Private Limited
          </Typography>
          <Typography sx={{ color: "#626d81", fontSize: 13 }}>
            {"\u00a9 2026 Sthanick. All rights reserved."}
          </Typography>
        </Stack>
      </Box> */}
      <UserFooter />
    </Box>
  );
};

const paragraphSx = {
  color: "#314054",
  fontSize: { xs: 16, md: 17 },
  lineHeight: 1.58,
  letterSpacing: 0.45,
  mb: 1.9,
};

const headingSx = {
  color: "#f05a1a",
  fontSize: { xs: 22, md: 25 },
  fontWeight: 900,
  letterSpacing: 0.5,
  borderBottom: "1px solid #f0dfcf",
  pb: 1,
  mb: 2.3,
};

const subheadingSx = {
  color: "#071326",
  fontSize: { xs: 17, md: 19 },
  lineHeight: 1.25,
  fontWeight: 900,
  letterSpacing: 0.15,
  mb: 0.8,
};

const TermsSection = ({ section }) => (
  <Box component="section" sx={{ mb: { xs: 4.2, md: 4.8 } }}>
    <Typography component="h2" sx={headingSx}>
      {section.title}
    </Typography>

    {section.blocks?.map((text) => (
      <Typography key={text} sx={paragraphSx}>
        {text}
      </Typography>
    ))}

    {section.checklist && (
      <Stack spacing={1.5} sx={{ mb: 2.8 }}>
        {section.checklist.map(([label, text]) => (
          <Stack
            key={label}
            direction="row"
            spacing={1.1}
            alignItems="flex-start"
          >
            <Box
              component="span"
              sx={{ color: "#f05a1a", fontSize: 17, lineHeight: 1.35 }}
            >
              {"\u2713"}
            </Box>
            <Typography sx={{ ...paragraphSx, mb: 0 }}>
              <Box
                component="strong"
                sx={{ color: "#2b3749", fontWeight: 900 }}
              >
                {label}:
              </Box>{" "}
              {text}
            </Typography>
          </Stack>
        ))}
      </Stack>
    )}

    {section.note && (
      <Box
        sx={{
          border: "1px solid #f5ddc0",
          bgcolor: "#fff8ef",
          borderRadius: 1,
          color: "#314054",
          fontSize: { xs: 15, md: 16 },
          lineHeight: 1.6,
          letterSpacing: 0.4,
          p: { xs: 2, md: 2.1 },
          mb: 3.8,
        }}
      >
        {section.note}
      </Box>
    )}

    {section.subsections?.map(([title, text]) => (
      <Box key={title} sx={{ mb: 2.2 }}>
        <Typography component="h3" sx={subheadingSx}>
          {title}
        </Typography>
        <Typography sx={paragraphSx}>{text}</Typography>
      </Box>
    ))}

    {section.bullets && (
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.4,
          mb: 2.7,
          "& li::marker": { color: "#f05a1a" },
        }}
      >
        {section.bullets.map((item) => (
          <Typography
            component="li"
            key={item}
            sx={{ ...paragraphSx, display: "list-item", mb: 0.7, pl: 0.4 }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
);

export default Termsconditions;
